import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { TypographyKeys } from '@thanx/uikit/theme/typography'
import { Toggle } from '@thanx/uikit/toggle'
import React, { useCallback } from 'react'

import Section from 'components/Section'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import CmsPage from 'scenes/Cms/components/CmsPage'
import PublishBanner from 'scenes/Cms/components/PublishBanner'
import { setAllowPostOrderFeedbackEmail } from 'slices/Cms/Settings/PostOrderFeedback'
import {
  selectAllowPostOrderFeedbackEmail,
  selectFormDisabled,
  selectIsFormSubmitting,
} from 'slices/Cms/Settings/PostOrderFeedback/selectors'
import { merchantTheme } from 'theme'
import { isPostOrderFeedbackHidden, isWholeEmailProvider } from './constants'
import { usePostOrderFeedbackFormInitialize } from './hooks/usePostOrderFeedbackFormInitialize'
import { usePostOrderFeedbackSubmitForm } from './hooks/usePostOrderFeedbackSubmitForm'
import Sidebar from './Sidebar'

const t = buildTranslate('cms.settings.post_order_feedback')

const PostOrderFeedback: React.FC<{ showTitle?: boolean }> = ({
  showTitle = true,
}) => {
  // All hooks must run unconditionally — the provider-hidden redirect lives
  // AFTER them per Rules of Hooks. The nav-side gates (CMS tab + live
  // settings route) hide the entry points for toast/square/qu (AI-1197); this
  // redirect protects against direct URL access if either gate is bypassed.
  const dispatch = useDispatch()
  const [css] = useStyletron()
  const merchant = useCurrentMerchant()

  usePostOrderFeedbackFormInitialize()

  const allowPostOrderFeedbackEmail = useSelector(
    selectAllowPostOrderFeedbackEmail
  )
  const isSubmitting = useSelector(selectIsFormSubmitting)
  const formDisabled = useSelector(selectFormDisabled)

  const handleSubmit = usePostOrderFeedbackSubmitForm()
  const handleToggleChange = useCallback(
    (checked: boolean) => {
      dispatch(setAllowPostOrderFeedbackEmail(checked))
    },
    [dispatch]
  )

  const greyTextCss = css({ color: merchantTheme.colors.grey50 })

  const provider = merchant?.ordering_provider
  const emailChannelCopyKey = isWholeEmailProvider(provider)
    ? 'whole_email'
    : 'thumbs_prompt'
  const emailChannelLabel = t(`email_channel.${emailChannelCopyKey}.label`)
  const emailChannelDescription = t(
    `email_channel.${emailChannelCopyKey}.description`
  )

  if (isPostOrderFeedbackHidden(merchant?.ordering_provider)) {
    return <Redirect to="/" />
  }

  return (
    <CmsPage
      showTitle={showTitle}
      title={t('title')}
      description={t('description')}
      sidebar={
        <Sidebar
          isSubmitting={isSubmitting}
          formDisabled={formDisabled}
          handleSubmit={handleSubmit}
        />
      }
    >
      <Section className="p-m mb-m">
        <Text className="mb-s d-block" typography={TypographyKeys.Header4}>
          {t('email_channel.title')}
        </Text>
        <div className="d-flex align-items-start">
          <Toggle
            ariaLabel={emailChannelLabel}
            checked={allowPostOrderFeedbackEmail}
            onChange={handleToggleChange}
            className="me-3"
            disabled={formDisabled}
          />
          <div className="d-flex flex-column">
            <Text
              className={`mb-xs ${formDisabled ? greyTextCss : ''}`}
              typography={TypographyKeys.BodyText3}
            >
              {emailChannelLabel}
            </Text>
            <Text
              className={`mb-xs ${greyTextCss}`}
              typography={TypographyKeys.BodyText4}
            >
              {emailChannelDescription}
            </Text>
          </div>
        </div>
      </Section>

      <PublishBanner
        buttonText={t('publish')}
        isLoading={isSubmitting}
        disabled={formDisabled}
        onClick={handleSubmit}
      />
    </CmsPage>
  )
}

export default PostOrderFeedback
